import React, { useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  TablePaginatedV8,
  CellYesNoV8,
  EyeColumnV8,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { Link, useLocation } from 'react-router-dom';
import { generatePath, useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  isActiveColumn,
  lastDateUserColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { apiBase } from 'shared-pages/common/api-helpfunc';
import { gqlDeleteEdoc } from '../graphql';
import { useMutation } from '@apollo/client';

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;

  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [deleteEdoc] = useMutation(gqlDeleteEdoc);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('JobtypesForm'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      // EyeColumnV8(rowClicked),
      {
        id: 'edocseriesId',
        header: t('Σειρά Αρίθμησης'),
        accessorKey: 'edocseriesId',
        orderByField: 'edocseriesId',
        columnName: t('Σειρά Αρίθμησης'),
        cell: ({ row }) => <div>{row.original.edocSeries?.name}</div>,
        meta: { type: 'text' },
        size: 20,
      },
      {
        id: 'edocNumber',
        header: t('Κωδικός Εντύπου'),
        accessorKey: 'edocNumber',
        orderByField: 'edocNumber',
        columnName: t('Κωδικός Εντύπου'),
        cell: ({ row }) => (
          <div>
            {row.original.edocPrefix} {row.original.edocNumber}
          </div>
        ),
        meta: { type: 'text' },
        size: 20,
      },
      {
        id: 'jsonContent',
        header: t('Περιεχόμενο'),
        accessorKey: 'jsonContent',
        orderByField: 'jsonContent',
        columnName: t('Περιεχόμενο'),
        cell: ({ row }) => {
          const rec = row.original;
          const obj = JSON.parse(rec.jsonContent);

          if (rec.edocTypeId === 1 && obj?.collection) {
            const url = `${apiBase()}/pdf/waste-reception-certificate/${rec.referenceId}`;
            return (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <strong>{obj?.collection?.code}</strong>: {obj?.collection['collectionpoint.name']}
              </a>
            );
          }
          return <div>{row.original.jsonContent}</div>;
        },
        meta: { type: 'text' },
        size: 30,
      },
      // isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      // lastDateUserColumn(t),
    ],
    [t, i18n.language],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => tableRef.current || null);

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          topBorderRadius={false}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε την εγγραφή;</div>`,
            });
            if (!ans) return;
            const res = await deleteEdoc({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) return false; // some error returned
            refetch();
            toastSuccess('Διαγράφηκε η εγγραφή');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
